// Mapping layer between frontend snake_case interfaces and backend camelCase API types.
// Frontend components use TripCalculation (snake_case) throughout.
// Backend API uses TripCalculatorV1* (camelCase, protobuf/gRPC-gateway convention).

import {
  DriverTripCalculatorFormData,
  TripCalculation,
  TripSegment,
  TripSegmentLocation,
  TripSegmentAdditionalCost,
  LocationPairRoute,
} from "_interfaces/driverTripCalculator.interfaces";
import {
  TripCalculatorV1CalculateTripReq,
  TripCalculatorV1CalculateTripData,
  TripCalculatorV1TripSegmentRes,
  TripCalculatorV1TripSegmentLocationRes,
  TripCalculatorV1AdditionalCostRes,
} from "../trip-calculator";

// ─── Request: snake_case form → camelCase API ────────────────────────────────

export const toCalculateTripReq = (
  data: DriverTripCalculatorFormData,
  segmentRoutes: Record<number, { totalDistance: number; totalTime: number; pairs: LocationPairRoute[] }>,
): TripCalculatorV1CalculateTripReq => ({
  customer: data.customer,
  temperature: typeof data.temperature === "string" ? parseFloat(data.temperature) : data.temperature,
  plateNumber: data.plate_number,
  vehicleTypeId: data.vehicle_type_id,
  vehicleModelId: data.vehicle_model_id,
  segments: data.segments.map((segment, idx) => {
    const route = segmentRoutes[idx];
    return {
      segmentOrder: segment.segment_order,
      tripTypeId: segment.trip_type_id,
      freezerIdleHours: segment.freezer_idle_hours ?? undefined,
      restTime: segment.rest_time,
      distanceKm: route?.totalDistance ?? 0,
      estimatedTimeMinutes: route?.totalTime ?? 0,
      routePairs: (route?.pairs ?? []).map((pair) => ({
        distanceKm: pair.distance_km,
        estimatedTimeMinutes: pair.estimated_time_minutes,
        routePolyline: pair.route_polyline,
      })),
      locations: segment.locations.map((loc) => ({
        name: loc.name,
        lat: loc.lat,
        lng: loc.lng,
        avoidToll: loc.avoid_toll,
      })),
      additionalCosts: segment.additional_costs.map((cost) => ({
        costType: cost.cost_type,
        amount: typeof cost.amount === "string" ? parseFloat(cost.amount) : cost.amount,
      })),
    };
  }),
});

// ─── Response: camelCase API → snake_case TripCalculation ────────────────────

const mapLocation = (loc: TripCalculatorV1TripSegmentLocationRes): TripSegmentLocation => ({
  id: loc.id,
  start_location_name: loc.startLocationName ?? "",
  start_location_lat: loc.startLocationLat ?? 0,
  start_location_lng: loc.startLocationLng ?? 0,
  end_location_name: loc.endLocationName ?? "",
  end_location_lat: loc.endLocationLat ?? 0,
  end_location_lng: loc.endLocationLng ?? 0,
  avoid_toll: loc.avoidToll ?? false,
  distance_km: loc.distanceKm ?? 0,
  estimated_time_minutes: loc.estimatedTimeMinutes ?? 0,
  route_polyline: loc.routePolyline,
});

const mapAdditionalCost = (cost: TripCalculatorV1AdditionalCostRes): TripSegmentAdditionalCost => ({
  id: cost.id,
  cost_type: cost.costType ?? "",
  amount: cost.amount ?? 0,
  created_at: cost.createdAt,
});

const mapSegment = (seg: TripCalculatorV1TripSegmentRes): TripSegment => ({
  id: seg.id,
  segment_order: seg.segmentOrder ?? 0,
  trip_type_id: seg.tripType?.id ?? "",
  trip_type: seg.tripType
    ? { id: seg.tripType.id ?? "", name: seg.tripType.name ?? "", value: seg.tripType.value ?? "" }
    : undefined,
  locations: (seg.locations ?? []).map(mapLocation),
  distance_km: seg.distanceKm ?? 0,
  estimated_time_minutes: seg.estimatedTimeMinutes ?? 0,
  recommended_time_minutes: seg.recommendedTimeMinutes ?? 0,
  fuel_usage: seg.fuelUsage ?? 0,
  fuel_price: seg.fuelPrice ?? 0,
  avg_speed: seg.avgSpeed ?? 0,
  working_hours: seg.workingHours ?? 0,
  trip_days: seg.tripDays ?? 0,
  total_basic_rate: seg.totalBasicRate ?? 0,
  incentive_rate: seg.incentiveRate ?? 0,
  solar_perjalanan: seg.solarPerjalanan ?? 0,
  total_freezer_fuel: seg.totalFreezerFuel ?? 0,
  freezer_fuel_efficiency: seg.freezerFuelEfficiency ?? 0,
  expected_fuel_cost: seg.expectedFuelCost ?? 0,
  driver_basic_rate: seg.driverBasicRate ?? 0,
  driver_insentif_rate: seg.driverInsentifRate ?? 0,
  driver_allowance: seg.driverAllowance ?? 0,
  rest_time: seg.restTime ?? 0,
  idle_time: seg.idleTime ?? 0,
  segment_total: seg.segmentTotal ?? 0,
  additional_costs: (seg.additionalCosts ?? []).map(mapAdditionalCost),
  freezer_idle_hours: seg.freezerIdleHours ?? null,
  created_at: seg.createdAt,
});

export const fromCalculateTripData = (data: TripCalculatorV1CalculateTripData): TripCalculation => ({
  id: data.id ?? "",
  customer: data.customer ?? "",
  temperature: data.temperature ?? 0,
  plate_number: data.plateNumber ?? "",
  vehicle_type: { id: data.vehicleType?.id ?? "", name: data.vehicleType?.name ?? "" },
  vehicle_model: { id: data.vehicleModel?.id ?? "", name: data.vehicleModel?.name ?? "" },
  segments: (data.segments ?? []).map(mapSegment),
  grand_total: data.grandTotal ?? 0,
  status: (data.status as "draft" | "completed") ?? "completed",
  created_at: data.createdAt ?? new Date().toISOString(),
  created_by: data.createdBy,
});
